<template>
    <transition :css="false" @enter="intro" @leave="outro">
        <div
            class="MainNav"
            :class="{'active' : !isInHome}"
            @mouseover="onMouseEnter"
            @mouseleave="onMouseLeave"

            @mousedown="onMouseDown"
            @touchstart="onMouseDown"

            @mousemove="onMouseMove"
            @touchmove="onMouseMove"

            @mouseup="onMouseUp"
            @touchend="onMouseUp"
            v-show="mainNavVisible"
        >

            <main-nav-list
                ref="mainNavList"
                :chapters="chapters"
                :is-dragging="isDragging"
                :item-height="itemHeight"
                @bttn-click="toggle"
            ></main-nav-list>

            <div class="MainNav-index" ref="indexIndicator">
                <small>{{activeIndex + 1}}</small>
                <small class="MainNav-indexTotal">
                    <span class="MainNav-indexDash"></span>
                    {{ chapters.length }}
                </small>
            </div>

        </div>
    </transition>
</template>

<script>
    import ChapterUtils from '@/app/mixins/ChapterUtils'
    import MainNavList from '@/app/components/MainNavList'
    import CloseLink from '@/app/components/CloseLink'
    import {mapGetters, mapMutations} from "vuex";
    import {TweenMax} from "gsap";
    import app from '@/app'
    import {BG_ACTIVE_INDEX, IS_DRAGGING, IS_IN_HOME, MAIN_NAV_VISIBLE} from "src/app/store/modules/background";

    const STRONG_FRICTION = 0.01;
    const WEAK_FRICTION = 5.0;

    export default {
        name: 'MainNav',

        mixins: [ChapterUtils],

        components: {
            MainNavList,
            CloseLink,
        },

        data: () => ({
            delta: 0,
            startMouseY: 0,
            itemHeight: 0,
            isOnTop: false,
            isHovered: false,
            isOnBottom: false,
        }),

        mounted() {
            this.position = 0;
            this.friction = WEAK_FRICTION;
            this.$nextTick(() => {
                this.measureLayout();
            })
        },
        methods: {

            intro(el, done) {
                this.toggleIndex(done);
                this.setInitialPosition(done);
                app.render.add(this.animate);
                app.resize.add(this.measureLayout);
            },

            outro(el, done) {
                this.toggleIndex(done)
                app.render.remove(this.animate);
                app.resize.remove(this.measureLayout);
            },

            setInitialPosition() {
                this.position = -(this.itemHeight * this.activeIndex);
            },

            measureLayout() {
                this.setItemHeight();
                this.topBound = 10;
                this.bottomBound = ~~(-(this.chapters.length - 1) * this.itemHeight);
            },

            onMouseEnter() {
                this.isHovered = true;
            },

            onMouseLeave() {
                this.isHovered = false;
                this.setDragging(false);
            },

            onMouseDown(e) {
                // Set hovered if mobile
                this.startDragTimer(e.touches && e.touches[0]);
                if (e.touches && e.touches[0]) {
                    this.startMouseY = ~~e.touches[0].clientY;
                } else {
                    this.startMouseY = ~~e.clientY;
                }
            },

            onMouseMove(e) {
                e.preventDefault();
                // Stop if its not dragging or in window
                if (!this.isDragging || !this.isHovered) {
                    this.delta = 0;
                    return;
                }
                // Handle drag
                if (e.touches && e.touches[0]) {
                    this.delta = ~~e.touches[0].clientY - this.startMouseY;
                    this.startMouseY = ~~e.touches[0].clientY;
                } else {
                    this.delta = ~~e.clientY - this.startMouseY;
                    this.startMouseY = ~~e.clientY;
                }
            },

            onMouseUp(e) {
                this.clearDragTimer(e.touches && e.touches[0]);
                setTimeout(() => {
                    this.centerActiveItem();
                }, 500)
            },

            startDragTimer(isHovered) {
                this.timer = setTimeout(() => {
                    this.setDragging(true);
                    this.isHovered = isHovered;
                }, 300)
            },

            clearDragTimer(isHovered) {
                clearTimeout(this.timer);
                this.timer = null;
                this.isHovered = isHovered;
                this.delta = 0;
                this.setDragging(false);
            },

            animate() {
                this.update();
                this.render();
            },

            update() {
                this.updateBounds();
                this.updateFriction();
            },

            updateBounds() {
                let {position, topBound, bottomBound} = this;
                this.passedTop = position > topBound;
                this.passedBottom = position < bottomBound;
            },

            updateActiveIndex() {
                const _newActiveIndex = Math.abs(~~((this.position - (this.itemHeight / 2)) / this.itemHeight));
                if (_newActiveIndex !== this.activeIndex) {
                    const _newChapter = this.getChapterById(_newActiveIndex);
                    this.updateActiveChapterId(_newChapter.id, _newChapter.number);
                }
            },

            centerActiveItem() {
                const distance = (window.innerHeight / 2) - (this.itemHeight / 2);
                const _activeItemY = ~~(this.itemHeight * (this.activeIndex + 2) + this.position);
                const toValue = ~~(_activeItemY - distance);
                this.position -= toValue
            },

            render() {
                const {MainNavListOutlines, MainNavListFill} = this.$refs.mainNavList.$refs;
                TweenMax.set([MainNavListOutlines, MainNavListFill], {
                    y: this.position += ~~(this.delta * this.friction),
                    force3D: true
                });
            },

            updateFriction() {
                const {passedTop, passedBottom} = this;
                // console.log(this.delta === 0 ? "still" : this.delta > 0 ? "down" : "up", passedTop, passedBottom)
                if (this.delta === 0) {
                    return;
                }
                if (this.delta > 0) {
                    // Going Down
                    if (passedTop) {
                        this.friction = STRONG_FRICTION;
                    } else {
                        this.friction = WEAK_FRICTION;
                        this.updateActiveIndex();
                    }
                } else {
                    // Going Up
                    if (passedBottom) {
                        this.friction = STRONG_FRICTION;
                    } else {
                        this.friction = WEAK_FRICTION;
                        this.updateActiveIndex();
                    }
                }
            },

            toggle() {
                this.setMainNavVisible(!this.mainNavVisible);
                this.setIsInHome(!this.isInHome);
            },

            toggleIndex(done) {
                const {isInHome} = this;
                TweenMax.to(this.$refs.indexIndicator, 0.3, {
                    x: isInHome ? "-100%" : "-10%",
                    delay: isInHome ? 0 : 0.8,
                    ease: isInHome ? Back.easeIn : Sine.easeOut,
                    onComplete: done,
                });
            },

            setItemHeight() {
                this.itemHeight = ~~window.innerHeight * 0.20;
            },

            ...mapMutations({
                setIsInHome: IS_IN_HOME,
                setDragging: IS_DRAGGING,
                setActiveIndex: BG_ACTIVE_INDEX,
                setMainNavVisible: MAIN_NAV_VISIBLE,
            })
        },
        computed: {
            ...mapGetters(["mainNavVisible", "isInHome", "activeIndex", "isDragging"]),
            mainNavList() {
                return this.$refs.mainNavList.$refs.MainNavListOutlines;
            },
        },
    }
</script>

<style src="styles/components/MainNav.styl" lang="stylus"></style>



// WEBPACK FOOTER //
// src/app/components/MainNav.vue